import React, { useState, useEffect } from 'react';
import { 
  calculateMilitaryRelocationLoan, 
  MilitaryRelocationLoanInputs, 
  formatCurrency, 
  formatPercentage
} from '../utils/militaryRelocationLoanCalculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

// Default values for the calculator
const DEFAULT_VALUES: MilitaryRelocationLoanInputs = {
  vehiclePrice: 35000,
  downPayment: 7000,
  loanTerm: 60,
  interestRate: 6.5,
  serviceStatus: 'active',
  isOverseasDeployment: false,
  isPostingRelocation: true,
  relocationDistance: 500,
  relocationType: 'interstate',
  balloonPayment: 0,
  applicationFee: 395,
  annualFee: 120,
  includeDHASubsidy: false
};

const MilitaryRelocationLoanCalculator: React.FC = () => {
  // State for form inputs
  const [inputs, setInputs] = useState<MilitaryRelocationLoanInputs>(DEFAULT_VALUES);
  const [results, setResults] = useState<any>(null);
  const [chartData, setChartData] = useState<any>([]);
  const [showAllFAQs, setShowAllFAQs] = useState<boolean>(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  
  // Calculate loan on first render and whenever inputs change
  useEffect(() => {
    handleCalculate();
  }, []);
  
  // Handle input changes
  const handleInputChange = (name: string, value: any) => {
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle number input changes (convert string to number)
  const handleNumberChange = (name: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    handleInputChange(name, numValue);
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (name: string) => {
    handleInputChange(name, !inputs[name as keyof MilitaryRelocationLoanInputs]);
  };
  
  // Handle dropdown changes
  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleInputChange(name, value);
  };
  
  // Calculate loan details
  const handleCalculate = () => {
    const calculationResults = calculateMilitaryRelocationLoan(inputs);
    setResults(calculationResults);
    
    // Prepare data for the chart
    const chartDataArray = calculationResults.amortizationSchedule
      .filter((row: any, index: number) => index % 12 === 0 || index === calculationResults.amortizationSchedule.length - 1)
      .map((row: any) => ({
        month: row.month,
        balance: row.balance,
        totalInterest: calculationResults.amortizationSchedule
          .slice(0, row.month)
          .reduce((sum: number, r: any) => sum + r.interest, 0)
      }));
      
    setChartData(chartDataArray);
  };
  
  // Toggle FAQ expansion
  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };
  
  // Calculate potential savings
  const calculateSavings = () => {
    if (!results) return 0;
    
    // Calculate standard loan (without military benefits)
    const standardLoanInputs = {
      ...inputs,
      serviceStatus: 'civilian',
      isOverseasDeployment: false,
      isPostingRelocation: false,
      includeDHASubsidy: false
    };
    
    const standardLoan = calculateMilitaryRelocationLoan(standardLoanInputs);
    
    // Return the difference in total cost
    return standardLoan.totalCost - results.totalCost;
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Military Relocation Car Loan Calculator | ADF Members & Veterans"
        description="Special auto loan calculator for ADF members and veterans who are relocating due to military postings. Calculate your loan with military-specific benefits."
        keywords="military car loan, ADF vehicle finance, military relocation loan, veteran car finance, defence force relocation, military posting car loan"
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Military Relocation Auto Loan Calculator
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Calculate your auto loan with special discounts and benefits for ADF members and veterans who are relocating due to military service.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calculator Input Section */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="pb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <span className="mr-2">📊</span> Loan Details
                </h2>
              </div>
              
              <div className="space-y-4">
                <Input
                  label="Vehicle Price"
                  type="number"
                  value={inputs.vehiclePrice.toString()}
                  onChange={(value) => handleNumberChange('vehiclePrice', value)}
                  unit="$"
                />
                
                <Input
                  label="Down Payment"
                  type="number"
                  value={inputs.downPayment.toString()}
                  onChange={(value) => handleNumberChange('downPayment', value)}
                  unit="$"
                />
                
                <Input
                  label="Loan Term"
                  type="number"
                  value={inputs.loanTerm.toString()}
                  onChange={(value) => handleNumberChange('loanTerm', value)}
                  unit="months"
                />
                
                <Input
                  label="Interest Rate"
                  type="number"
                  value={inputs.interestRate.toString()}
                  onChange={(value) => handleNumberChange('interestRate', value)}
                  unit="%"
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Status
                  </label>
                  <select
                    name="serviceStatus"
                    value={inputs.serviceStatus}
                    onChange={handleDropdownChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="active">Active ADF Member</option>
                    <option value="veteran">Veteran</option>
                    <option value="reserve">ADF Reserve</option>
                    <option value="civilian">Civilian (No Military Status)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relocation Type
                  </label>
                  <select
                    name="relocationType"
                    value={inputs.relocationType}
                    onChange={handleDropdownChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="interstate">Interstate</option>
                    <option value="regional">Regional</option>
                    <option value="metropolitan">Metropolitan</option>
                    <option value="overseas">Overseas Posting</option>
                  </select>
                </div>
                
                <Input
                  label="Relocation Distance"
                  type="number"
                  value={inputs.relocationDistance.toString()}
                  onChange={(value) => handleNumberChange('relocationDistance', value)}
                  unit="km"
                />
                
                <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Relocation Circumstances</h3>
                
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={inputs.isPostingRelocation}
                    onChange={() => handleCheckboxChange('isPostingRelocation')}
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Military Posting Relocation
                  </label>
                </div>
                
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={inputs.isOverseasDeployment}
                    onChange={() => handleCheckboxChange('isOverseasDeployment')}
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Returning from Overseas Deployment
                  </label>
                </div>
                
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={inputs.includeDHASubsidy}
                    onChange={() => handleCheckboxChange('includeDHASubsidy')}
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Include DHA Housing Subsidy
                  </label>
                </div>
                
                <div className="border-t border-gray-200 my-4 pt-4">
                  <h3 className="text-md font-semibold text-gray-700 mb-2">Additional Costs</h3>
                  
                  <Input
                    label="Application Fee"
                    type="number"
                    value={inputs.applicationFee.toString()}
                    onChange={(value) => handleNumberChange('applicationFee', value)}
                    unit="$"
                  />
                  
                  <Input
                    label="Annual Fee"
                    type="number"
                    value={inputs.annualFee.toString()}
                    onChange={(value) => handleNumberChange('annualFee', value)}
                    unit="$"
                  />
                  
                  <Input
                    label="Balloon Payment"
                    type="number"
                    value={inputs.balloonPayment.toString()}
                    onChange={(value) => handleNumberChange('balloonPayment', value)}
                    unit="$"
                  />
                </div>
                
                <button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg mt-4"
                  onClick={handleCalculate}
                >
                  Calculate
                </button>
              </div>
            </div>
          </div>
          
          {/* Results Section */}
          <div className="lg:col-span-2">
            {results && (
              <div className="space-y-6">
                {/* Main Results Card */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Loan Summary</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-lg text-gray-900">Monthly Payment</h3>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(results.monthlyPayment)}</p>
                      <p className="text-sm text-gray-500">
                        Original rate: {formatPercentage(results.originalRate)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Your rate: {formatPercentage(results.discountedRate)}
                        {results.rateDiscount > 0 && ` (${formatPercentage(results.rateDiscount)} discount)`}
                      </p>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-lg text-gray-900">Military Savings</h3>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(calculateSavings())}</p>
                      <p className="text-sm text-gray-500">
                        Over the life of your loan compared to standard rates
                      </p>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 my-6 pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Loan Breakdown</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Vehicle Price</h4>
                        <p className="text-lg font-semibold">{formatCurrency(inputs.vehiclePrice)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Down Payment</h4>
                        <p className="text-lg font-semibold">{formatCurrency(inputs.downPayment)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Loan Amount</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.loanAmount)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Loan Term</h4>
                        <p className="text-lg font-semibold">{inputs.loanTerm} months</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Total Interest</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.totalInterest)}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Total Cost</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.totalCost)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 my-6 pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Relocation Benefits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-green-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Relocation Allowance</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.relocationBenefits.relocationAllowance)}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Transport Savings</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.relocationBenefits.vehicleTransportSavings)}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">DHA Subsidy</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.relocationBenefits.dhaSubsidy)}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500">Total Benefits</h4>
                        <p className="text-lg font-semibold">{formatCurrency(results.relocationBenefits.totalRelocationBenefits)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 my-6 pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Eligible Benefits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {results.eligibleBenefits.map((benefit: string, index: number) => (
                        <div key={index} className="p-2 bg-green-50 border border-green-100 rounded-lg flex items-center">
                          <span className="text-green-500 mr-2">✓</span>
                          <span className="text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Chart Section */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Loan Progress</h2>
                  <div className="h-80 flex flex-col justify-center items-center">
                    {chartData.length > 0 ? (
                      <div className="w-full">
                        <div className="bg-gray-200 h-8 rounded-lg overflow-hidden mb-4">
                          <div 
                            className="bg-blue-600 h-full" 
                            style={{ 
                              width: `${100 - ((chartData[chartData.length - 1].balance / results.loanAmount) * 100)}%` 
                            }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <div>Start: {formatCurrency(results.loanAmount)}</div>
                          <div>Remaining: {formatCurrency(chartData[chartData.length - 1].balance)}</div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500">Chart data not available</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {!results && (
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <p className="text-gray-600">
                  Enter your loan details and click "Calculate" to see your military relocation loan breakdown.
                </p>
              </div>
            )}
            
            {/* Information Section */}
            <div className="bg-white p-6 rounded-lg shadow-md mt-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">About Military Relocation Auto Loans in Australia</h2>
              <p className="text-gray-700 mb-4">
                Military personnel and veterans in Australia often face unique challenges when relocating for service. This calculator helps you understand the financial benefits available specifically for ADF members and veterans who need to purchase a vehicle at a new posting location.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Key Benefits Include:</h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
                <li>Rate discounts for ADF members (typically 0.5-1.0% below standard rates)</li>
                <li>Reduced or waived application fees for eligible service members</li>
                <li>Relocation allowances that can be used toward vehicle purchases</li>
                <li>Vehicle transport cost savings factored into your loan calculations</li>
                <li>DHA housing subsidies that may free up more of your budget for vehicle payments</li>
              </ul>
              
              <div className="overflow-x-auto mt-6">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-2 px-4 border text-left">Relocation Type</th>
                      <th className="py-2 px-4 border text-left">Benefit Value</th>
                      <th className="py-2 px-4 border text-left">Eligibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 px-4 border">Interstate Posting</td>
                      <td className="py-2 px-4 border">$1,500 - $3,000</td>
                      <td className="py-2 px-4 border">Active ADF members</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-4 border">Overseas Return</td>
                      <td className="py-2 px-4 border">$2,500 - $5,000</td>
                      <td className="py-2 px-4 border">Active ADF members returning from overseas deployment</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-4 border">Regional Posting</td>
                      <td className="py-2 px-4 border">$1,000 - $2,000</td>
                      <td className="py-2 px-4 border">Active ADF members and reserves</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* FAQ Section */}
            <div className="bg-white p-6 rounded-lg shadow-md mt-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-4">
                    <button
                      className="flex justify-between items-center w-full text-left py-2"
                      onClick={() => toggleFAQ(index)}
                    >
                      <span className="font-medium text-gray-800">{faq.question}</span>
                      <span className="w-5 h-5 text-gray-500">
                        {expandedFAQ === index ? '▲' : '▼'}
                      </span>
                    </button>
                    
                    {expandedFAQ === index && (
                      <div className="mt-2 text-gray-600 text-sm">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <button
                className="text-blue-600 hover:text-blue-800 mt-4 font-medium"
                onClick={() => setShowAllFAQs(!showAllFAQs)}
              >
                {showAllFAQs ? 'Show Less' : 'Show All FAQs'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// FAQ data
const faqs = [
  {
    question: "What special loan rates are available to ADF members?",
    answer: "ADF members typically qualify for interest rate discounts of 0.5-1.0% below standard rates on car loans. Active duty members may receive larger discounts, especially when relocating due to service requirements."
  },
  {
    question: "How does the calculator account for relocation benefits?",
    answer: "The calculator factors in official ADF relocation allowances, potential vehicle transport savings, and housing subsidies that may be available. These benefits can significantly reduce your effective vehicle costs when relocating."
  },
  {
    question: "Am I eligible for benefits as a veteran or reserve member?",
    answer: "Yes, veterans and reserve members generally qualify for some benefits, though they may be at a reduced rate compared to active duty personnel. Veterans with service-related injuries or disabilities often qualify for additional concessions."
  },
  {
    question: "How are the relocation distances calculated?",
    answer: "The relocation distance you enter helps determine potential transport savings and eligibility for certain distance-based benefits. For interstate relocations, typical benefits increase with distance traveled."
  },
  {
    question: "Can I combine military benefits with other car loan discounts?",
    answer: "Yes, in most cases ADF benefits can be combined with dealer incentives, manufacturer rebates, and seasonal promotions. However, some lender-specific military discounts may not stack with other rate reductions."
  }
];

// ADF Benefits for the DHA subsidy calculation display
const ADF_BENEFITS = {
  DHA_MONTHLY_SUBSIDY: 250
};

export default MilitaryRelocationLoanCalculator; 